// Load Roboto as fallback font (used for italic text per Lidingö design system)
if (typeof document !== 'undefined') {
  const robotoFont = document.createElement('link');
  robotoFont.rel = 'stylesheet';
  robotoFont.href = 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,400;0,500;0,700;1,400&display=swap';
  document.head.appendChild(robotoFont);
}

// Colors extracted from muniprod.lidingo.se :root CSS variables
export const lidingoLight = {
  name: 'lidingo:light',
  extends: 'citizen:light',
  cssPath: 'custom/styles/lid/lidingo-light.theme',
  vuetify: {
    defaults: {
      VCard: {
        elevation: 0,
        flat: true,
      },
      VSheet: {
        elevation: 0,
      },
      VBtn: {
        elevation: 0,
        variant: 'flat',
      },
      VBreadcrumbs: {
        divider: '·',
      },
      HItemCard: {
        HScopedItemCardActionBtn: {
          color: 'action-button',
          variant: 'flat',
        },
        HScopedItemCardMenuBtn: {
          color: 'action-button',
          variant: 'flat',
        },
      },
      HActionButton: {
        flat: true,
        elevation: 0,
      },
    },
    theme: {
      dark: false,
      colors: {
        // Primary palette from --color-primary-*
        primary: '#002b49',
        'on-primary': '#FFFFFF',
        'primary-lighten-1': '#1f445f',
        'primary-lighten-2': '#8095A4',
        'primary-lighten-3': '#CCD5DB',

        // Secondary from --color-secondary-*
        secondary: '#431f1e',
        'secondary-lighten-1': '#8F7473',
        'secondary-lighten-2': '#B8A9A8',
        'secondary-lighten-3': '#DACECE',

        // Semantic colors from website
        error: '#d43f3f',
        'error-lighten-1': '#d43f3f',
        'error-lighten-2': '#e89c9c',
        'on-error-lighten-2': '#000000',
        'error-lighten-3': '#f5e4e3',
        success: '#697d40',
        'success-lighten-1': '#8b996b',
        'success-lighten-2': '#b5c4a0',
        'success-lighten-3': '#dfe7d3',
        warning: '#ECD898',
        'warning-lighten-1': '#ECD898',
        'warning-lighten-2': '#f3e7bf',
        'warning-lighten-3': '#faf3e0',

        // Surfaces — white cards on light grey background
        surface: '#FFFFFF',
        'on-surface': '#333333',
        'surface-variant': '#e5e5e5',
        'on-surface-variant': '#333333',
        'neutral-100': '#FFFFFF',
        'on-neutral-100': '#333333',
        background: '#E8ECEF',
        'background-3': '#E8ECEF',
        'on-background-3': '#333333',
        'background-4': '#FFFFFF',
        'on-background-4': '#333333',

        // Breadcrumbs
        'breadcrumb-hover': '#E8ECEF',
        'on-breadcrumb': '#002b49',

        // Bulletin / sidebar
        bulletin: '#FFFFFF',
        'on-bulletin': '#333333',
        'bulletin-board': '#f5f5f5',
        'on-bulletin-board': '#333333',
        'start-page': '#E8ECEF',
        'on-start-page': '#333333',

        // Neutrals from website
        black: '#000000',
        'black-lighten-1': '#3d3d3d',
        gray: '#707070',
        'gray-lighten-1': '#a3a3a3',
        'gray-lighten-2': '#e5e5e5',
        white: '#FFFFFF',
        'white-darken-1': '#fcfcfc',

        // Category colors (soft pastels for card backgrounds)
        foretag: '#CCD5DB',
        'on-foretag': '#002b49',
        fastigheter: '#DACECE',
        fullmakter: '#D7D3CE',
        ovriga: '#E7EBEB',
        utbildning: '#CCD5DB',
        omsorg: '#ECD898',
        forening: '#D7D3CE',
        adshavare: '#E7EBEB',

        // Sector colors
        'sector-privat': '#CCD5DB',
        'sector-företag': '#DACECE',
        'on-sector-privat': '#002b49',
        'on-sector-företag': '#431f1e',

        // UI elements
        red: '#d43f3f',
        green: '#697d40',
        skog: '#697d40',
        strand: '#431f1e',
        'strand-lighten-1': '#8F7473',
        'strand-lighten-2': '#B8A9A8',
        disabled: '#a3a3a3',
        loader: '#002b49',
        'loader-background': '#e5e5e5',
        scrollbar: '#002b49',

        // Badges — notification red from muniprod.lidingo.se: rgb(200, 98, 98)
        'badge-disabled': '#a3a3a3',
        'badge-primary': '#C86262',
        'on-badge-primary': '#FFFFFF',
        'badge-secondary': '#431f1e',
        'badge-tertiary': '#C86262',

        // Text
        'text-color-primary': '#333333',
        'text-color-secondary': '#565656',

        // Toolbar (header)
        toolbar: '#002b49',
        'on-toolbar': '#FFFFFF',

        // Action buttons
        'action-button': '#5d798b',
        'on-action-button': '#FFFFFF',

        // Process states
        'process-state-active': '#431f1e',

        // Chips — dark navy matching header
        'chip-active': '#002b49',
        'on-chip-active': '#FFFFFF',

        // Bulletins
        'bulletin-informational': '#FFFFFF',
        'on-bulletin-informational': '#333333',
        'bulletin-warning': '#faf3e0',
        'on-bulletin-warning': '#333333',
        'bulletin-critical': '#f5e4e3',
        'on-bulletin-critical': '#333333',
        'bulletin-skipped': '#f5f5f5',
        'on-bulletin-skipped': '#333333',
        'dialog-header-button': '#002b49',

        // Page header backgrounds — same blue-tinted bg as start page
        'page-companies-header-bg': '#E8ECEF',
        'on-page-companies-header-bg': '#333333',
        'page-properties-header-bg': '#E8ECEF',
        'on-page-properties-header-bg': '#333333',
        'page-authorizations-header-bg': '#E8ECEF',
        'on-page-authorizations-header-bg': '#333333',
        'page-children-header-bg': '#E8ECEF',
        'on-page-children-header-bg': '#333333',
        'page-enrollments-header-bg': '#E8ECEF',
        'on-page-enrollments-header-bg': '#333333',
        'page-employments-header-bg': '#E8ECEF',
        'on-page-employments-header-bg': '#333333',
        'page-eservices-header-bg': '#E8ECEF',
        'on-page-eservices-header-bg': '#333333',
        'page-cases-header-bg': '#E8ECEF',
        'on-page-cases-header-bg': '#333333',

        // Item card border colors
        'card-company': '#CCD5DB',
        'card-property': '#DACECE',
        'card-authorization': '#D7D3CE',
        'card-child': '#CCD5DB',
        'card-enrollment': '#CCD5DB',
        'card-employment': '#E7EBEB',
        'card-case': '#CCD5DB',
      },
    },
  },
};
